import { BetterAuthUserSchema } from "@ai-teach/types";
import { ErrorReporter } from "@ai-teach/utils";
import { type ReactNode, createContext, useContext } from "react";
import { authClient } from "../lib/auth-client";
import { ADMIN_ROLES } from "../lib/constants/roles";

interface AuthContextType {
  user: {
    id: string;
    email: string;
    name: string;
    username?: string;
    role?: string;
    authSource?: "local" | "mybestuser";
    myBestUserId?: string | null;
    myBestUserRole?: string | null;
  } | null;
  isAuthenticated: boolean;
  isPending: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  signIn: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; error?: string; user?: { role?: string } }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const session = authClient.useSession() as { data: { user: unknown } | null; isPending: boolean };

  const signOut = async () => {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          // Use window.location since AuthProvider is outside RouterProvider
          window.location.href = "/login";
        },
      },
    });
  };

  const signIn = async (
    username: string,
    password: string
  ): Promise<{ success: boolean; error?: string; user?: { role?: string } }> => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4001/graphql";
      const BASE_URL = API_URL.replace(/\/graphql$/, "");

      const response = await fetch(`${BASE_URL}/api/auth/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || "Login failed" };
      }

      return { success: true, user: data.user };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "An unexpected error occurred",
      };
    }
  };

  // Validate user data from Better Auth with Zod
  const validatedUser = session.data?.user
    ? (() => {
        const result = BetterAuthUserSchema.safeParse(session.data.user);
        if (!result.success) {
          ErrorReporter.critical(
            new Error(`Invalid user data from Better Auth: ${result.error.message}`),
            {
              action: "validateUserData",
              screen: "AuthProvider",
              metadata: { error: result.error.message },
            }
          );
          return null;
        }
        return {
          id: result.data.id,
          email: result.data.email,
          name: result.data.name,
          username: result.data.username,
          role: result.data.role,
          authSource: result.data.authSource,
          myBestUserId: result.data.myBestUserId,
          myBestUserRole: result.data.myBestUserRole,
        };
      })()
    : null;

  // Check if user has admin role (content editing permissions)
  const isAdmin = validatedUser ? ADMIN_ROLES.includes(validatedUser.role || "") : false;

  return (
    <AuthContext.Provider
      value={{
        user: validatedUser,
        isAuthenticated: !!session.data,
        isPending: session.isPending,
        isAdmin,
        signOut,
        signIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
